import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import axiosInstance from '@/axios';

const HeroBanner = () => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(true);
  const { t } = useTranslation();
  const { loggedIn } = useSelector((state) => state.authentication);
  const navigate = useNavigate();

  const [currentSlide, setCurrentSlide] = useState(0);
  const scrollRef = React.useRef(null);

  useEffect(() => {
    fetchHomePreview();
  }, []);

  const fetchHomePreview = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/public/home/banner');
      setSlides(response?.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch homebanner:', error);
    } finally {
      setLoading(false);
    }
  };

  // Buttery-smooth custom ease-out-cubic scroll transition
  const smoothScrollTo = (element, target, duration = 500) => {
    const start = element.scrollLeft;
    const change = target - start;
    const startTime = performance.now();

    const easeOutCubic = (t) => {
      return 1 - Math.pow(1 - t, 3);
    };

    const animateScroll = (currentTime) => {
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);
      const easedProgress = easeOutCubic(progress);
      
      element.scrollLeft = start + change * easedProgress;

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      } else {
        element.scrollLeft = target;
      }
    };

    requestAnimationFrame(animateScroll);
  };

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, clientWidth, scrollWidth } = scrollRef.current;
      const maxScrollLeft = scrollWidth - clientWidth;

      let newIndex = 0;

      if (maxScrollLeft > 0 && scrollLeft >= maxScrollLeft - 24) {
        newIndex = slides.length - 1;
      } else {
        const itemWidth = scrollRef.current.firstChild ? scrollRef.current.firstChild.offsetWidth : clientWidth;
        const gap = 16;
        newIndex = Math.round(scrollLeft / (itemWidth + gap));
      }

      if (newIndex !== currentSlide && newIndex >= 0 && newIndex < slides.length) {
        setCurrentSlide(newIndex);
      }
    }
  };

  const goToSlide = (index) => {
    if (scrollRef.current) {
      const { clientWidth, scrollWidth } = scrollRef.current;
      const maxScroll = scrollWidth - clientWidth;

      const card = scrollRef.current.children[index];
      if (card) {
        const cardOffset = card.offsetLeft;
        const cardWidth = card.offsetWidth;
        // Exact center position calculation to prevent layout snapping jumps
        let targetScrollCentered = cardOffset - (clientWidth / 2) + (cardWidth / 2);
        
        if (targetScrollCentered < 0) targetScrollCentered = 0;
        if (targetScrollCentered > maxScroll) targetScrollCentered = maxScroll;

        smoothScrollTo(scrollRef.current, targetScrollCentered, 500);
        setCurrentSlide(index);
      }
    }
  };

  const scrollByAmount = (direction) => {
    if (scrollRef.current) {
      let nextIndex = direction === 'left' ? currentSlide - 1 : currentSlide + 1;
      if (nextIndex < 0) nextIndex = 0;
      if (nextIndex >= slides.length) nextIndex = slides.length - 1;
      goToSlide(nextIndex);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (!slides || slides.length === 0) {
    return null;
  }

  return (
    <div className="relative mt-10 max-w-10xl mx-1 select-none overflow-hidden px-4 md:px-6">
      {/* Top Enroll Bar */}
      {/* {visible && !loggedIn && (
        <div
          className={`absolute top-0 left-0 right-0 py-2.5 px-4 flex justify-between items-center text-white z-40 bg-zinc-800/95 backdrop-blur-sm shadow-md transition-all duration-300`}
        >
          <div className="flex-1 text-center">
            <span className="text-xs sm:text-sm font-medium">
              {t("courseEnroll")}{" "}
              <Link to="/login" className="font-semibold underline hover:text-yellow-400 transition-colors">
                Enroll today!
              </Link>
            </span>
          </div>
          <button
            className="hover:text-red-400 transition-colors text-sm font-extrabold px-2 py-1"
            onClick={() => setVisible(false)}
          >
            ✕
          </button>
        </div>
      )} */}

      {/* Horizontally Scrollable Cards Container */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory scrollbar-none pb-16 pt-10 px-4 md:px-12 select-none"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
          perspective: '1200px',
          transformStyle: 'preserve-3d'
        }}
      >
        {slides.map((slide, index) => {
          const isActive = index === currentSlide;
          
          let transformStyle = "";
          if (index === currentSlide) {
            transformStyle = "translate3d(0, 0, 40px) rotateY(0deg) scale(1.02)";
          } else if (index < currentSlide) {
            transformStyle = "translate3d(20px, 0, -80px) rotateY(18deg) scale(0.93)";
          } else {
            transformStyle = "translate3d(-20px, 0, -80px) rotateY(-18deg) scale(0.93)";
          }

          return (
            <div
              key={slide.id}
              onClick={(e) => {
                if (!isActive) {
                  e.stopPropagation();
                  goToSlide(index);
                }
              }}
              className={`w-[88vw] sm:w-[500px] md:w-[68%] lg:w-[58%] flex-shrink-0 snap-center rounded-[2.5rem] overflow-hidden border relative flex flex-col justify-between p-6 sm:p-8 min-h-[330px] md:min-h-[350px] select-none text-white transition-all duration-600 bg-zinc-950 ${isActive
                  ? 'border-white/20 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.9)] z-20 opacity-100 cursor-default'
                  : 'border-white/5 shadow-none z-10 opacity-40 cursor-pointer hover:opacity-60'
                }`}
              style={{
                transform: transformStyle,
                transformStyle: 'preserve-3d',
                backfaceVisibility: 'hidden'
              }}
            >
              {/* Background Template Image */}
              {slide.backgroundTemplateUrl && (
                <div
                  className="absolute inset-0 bg-cover bg-center bg-no-repeat pointer-events-none z-0"
                  style={{
                    backgroundImage: `url("${encodeURI(slide.backgroundTemplateUrl)}")`
                  }}
                />
              )}

              {/* Background Template Overlays */}
              {slide.backgroundTemplate === 'dots' && (
                <div
                  className="absolute inset-0 opacity-[0.08] pointer-events-none z-0"
                  style={{
                    backgroundImage: 'radial-gradient(rgba(255,255,255,0.4) 1px, transparent 1px)',
                    backgroundSize: '24px 24px'
                  }}
                />
              )}

              {slide.backgroundTemplate === 'grid' && (
                <div
                  className="absolute inset-0 opacity-[0.05] pointer-events-none z-0"
                  style={{
                    backgroundImage: `
                      linear-gradient(to right, rgba(255,255,255,0.2) 1px, transparent 1px),
                      linear-gradient(to bottom, rgba(255,255,255,0.2) 1px, transparent 1px)
                    `,
                    backgroundSize: '36px 36px'
                  }}
                />
              )}

              {slide.backgroundTemplate === 'mesh' && (
                <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
                  <div className="absolute -top-24 -left-24 w-64 h-64 rounded-full bg-indigo-500/20 blur-[80px]" />
                  <div className="absolute top-1/2 left-1/3 w-48 h-48 rounded-full bg-pink-500/15 blur-[70px]" />
                  <div className="absolute -bottom-20 -right-20 w-64 h-64 rounded-full bg-cyan-500/20 blur-[80px]" />
                </div>
              )}

              {/* Adjustable Linear Gradient Overlay */}
              <div
                className="absolute inset-0 pointer-events-none z-0"
                style={{
                  background: slide.backgroundColor || 'linear-gradient(135deg, #1e1b4b 0%, #311042 100%)',
                  opacity: (slide.gradientIntensity !== undefined ? slide.gradientIntensity : 80) / 100
                }}
              />

              {/* Card Content wrapper */}
              <div className="relative z-10 flex flex-col justify-between h-full w-full">
                {/* Top: Category and Badges */}
                <div className="flex justify-between items-center gap-2 mb-5">
                  <span className="bg-white/10 backdrop-blur-md px-4 py-1.5 rounded-full text-xs sm:text-sm font-bold uppercase tracking-wider text-white border border-white/10">
                    {slide.category || 'Offer'}
                  </span>
                  {slide.badgeText && (
                    <span className="bg-amber-400 text-zinc-900 px-4 py-1.5 rounded-full text-xs sm:text-sm font-black uppercase tracking-wider shadow-sm animate-pulse">
                      {slide.badgeText}
                    </span>
                  )}
                </div>

                {/* Middle Content (Text + Illustration) */}
                <div className="flex gap-6 items-center justify-between flex-grow my-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight leading-tight line-clamp-2 mb-3">
                      {slide.title}
                    </h3>
                    <div
                      className="text-sm sm:text-base text-white/90 leading-relaxed font-normal line-clamp-3 prose prose-invert max-w-none prose-ul:list-disc prose-ul:pl-5 prose-li:my-1"
                      dangerouslySetInnerHTML={{ __html: slide.description }}
                    />
                  </div>

                  {/* Graphic layout inside card */}
                  <div className="flex-shrink-0">
                    {slide.offerType === 'circle-image' ? (
                      <div className="relative w-28 h-28 sm:w-36 sm:h-36 flex items-center justify-center">
                        <div className="absolute -inset-1 bg-gradient-to-tr from-cyan-400 to-indigo-500 rounded-full opacity-20 blur-md" />
                        <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full overflow-hidden border-4 border-white/20 shadow-lg relative">
                          <img
                            src={slide.imageUrl || "/placeholder.svg"}
                            alt="offer banner graphic"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>
                    ) : (
                      <div
                        className="relative w-28 h-28 sm:w-36 sm:h-36 rounded-full flex flex-col items-center justify-center shadow-lg border-2 border-white/20"
                        style={{
                          background: slide.offerColor || 'linear-gradient(to bottom, #f59e0b, #ea580c, #dc2626)'
                        }}
                      >
                        <div className="text-center text-white px-2 select-none">
                          {slide.originalPrice && (
                            <span className="block text-xs sm:text-sm line-through text-white/70 font-semibold">
                              ₹{slide.originalPrice}
                            </span>
                          )}

                          <span className="block text-lg sm:text-2xl font-black text-white leading-none my-0.5">
                            ₹{slide.discountedPrice || '7,499'}
                          </span>

                          <span className="block text-[10px] sm:text-xs font-bold opacity-90 uppercase tracking-widest">
                            {slide.billingTerm || '/year'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Bottom Action Button */}
                <div className="flex items-center justify-between gap-4 mt-5 pt-4 border-t border-white/10">
                  <button
                    onClick={() => navigate(slide.ctaLink || '/login')}
                    className="group bg-white/5 backdrop-blur-xl border border-white/20 hover:bg-white/20 hover:border-white/40 text-white font-bold px-6 py-2 rounded-full flex items-center gap-2 shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105"
                  >
                    <span>{slide.ctaText || 'Save now'}</span>
                    <span className="transition-transform duration-300 group-hover:translate-x-1">
                      →
                    </span>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Floating Indicators */}
      {/* {slides.length > 1 && (
        <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 z-30 flex items-center gap-1.5 bg-zinc-900/60 backdrop-blur-md px-4 py-2 rounded-full border border-white/10 shadow-2xl select-none">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? 'w-5 bg-yellow-400 shadow-md'
                  : 'w-2 bg-white/40 hover:bg-white/60'
              }`}
              aria-label={`Go to card ${index + 1}`}
            />
          ))}
        </div>
      )} */}

      {/* Navigation Arrows fixed to slide container edges */}
      {/* {slides.length > 1 && (
        <>
          <button
            onClick={() => scrollByAmount('left')}
            className="absolute left-2 md:left-6 top-1/2 transform -translate-y-1/2 z-30 p-3 bg-zinc-900/60 hover:bg-zinc-900/80 active:scale-95 border border-white/10 backdrop-blur-md rounded-full text-white transition-all shadow-xl"
            aria-label="Previous card"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          <button
            onClick={() => scrollByAmount('right')}
            className="absolute right-2 md:right-6 top-1/2 transform -translate-y-1/2 z-30 p-3 bg-zinc-900/60 hover:bg-zinc-900/80 active:scale-95 border border-white/10 backdrop-blur-md rounded-full text-white transition-all shadow-xl"
            aria-label="Next card"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )} */}
    </div>
  );
};

export default HeroBanner;
